import React from "react";
import { ArrowRight, Zap, MapPin, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { WHATSAPP_LINK } from "@/lib/constants";

const HERO_BG = "/img/operacion/hero-instalacion-grua.jpg";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex items-end overflow-hidden grain"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 z-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      {/* Overlays */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-[#0a0a0a]/85 via-[#0a0a0a]/70 to-[#0a0a0a]" />
      <div className="absolute inset-0 z-[1] bg-grid opacity-40" />

      {/* Top status bar */}
      <div className="absolute top-24 left-0 right-0 z-10 max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between text-xs uppercase tracking-[0.3em] text-[#a0aabf]">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 bg-[#22c55e] rounded-full pulse-dot" />
          <span>Operación 24/7</span>
        </div>
        <div className="hidden md:flex items-center gap-2">
          <MapPin className="w-3 h-3" />
          <span>Santander · Colombia</span>
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 pb-20 lg:pb-32 pt-40">
        <div className="grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-3 mb-8 border border-[#ff6b00]/40 bg-[#ff6b00]/10 px-4 py-2">
              <Zap className="w-3.5 h-3.5 text-[#ff6b00]" />
              <span className="text-[11px] font-mono uppercase tracking-[0.25em] text-[#ff6b00]">
                +20 Años · 1500+ Proyectos
              </span>
            </div>

            <h1
              data-testid="hero-title"
              className="font-display text-[44px] sm:text-6xl lg:text-[88px] leading-[0.95] font-bold tracking-tighter mb-8"
            >
              La energía
              <br />
              que <span className="italic font-light text-[#ff6b00]">no</span> se
              <br />
              detiene.
            </h1>

            <p className="text-lg lg:text-xl text-[#a0aabf] max-w-2xl leading-relaxed mb-10 font-light">
              Mantenimiento, reparación y venta de transformadores eléctricos
              tipo aceite en Bucaramanga, Santander y toda Colombia.
              Recuperamos la vida útil de sus equipos con tecnología de punta y
              cumplimiento normativo.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <a href="#cotizador" data-testid="hero-cta-primary">
                <Button className="group bg-[#ff6b00] hover:bg-[#e65a00] text-black font-bold uppercase tracking-wider text-sm rounded-none h-14 px-8 w-full sm:w-auto">
                  Cotizar mi transformador
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-cta-secondary"
              >
                <Button
                  variant="outline"
                  className="border-white/20 hover:border-white text-white hover:bg-white/5 bg-transparent font-bold uppercase tracking-wider text-sm rounded-none h-14 px-8 w-full sm:w-auto"
                >
                  Hablar con un técnico
                </Button>
              </a>
            </div>
          </div>

          {/* Right column — technical spec card */}
          <div className="lg:col-span-4">
            <div className="glass p-6 lg:p-7 relative">
              <div className="absolute -top-px left-6 right-6 h-px electric-line" />
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#a0aabf] mb-5 flex items-center justify-between">
                <span>Capacidades</span>
                <span className="font-mono text-[#ff6b00]">SPEC-001</span>
              </div>
              <div className="space-y-4">
                {[
                  ["Tipo Aceite", "5 – 3000 kVA"],
                  ["Tensión", "208V – 34.5kV"],
                  ["Norma", "RETIE · NTC"],
                ].map(([k, v]) => (
                  <div
                    key={k}
                    className="flex items-center justify-between pb-3 border-b border-white/5 last:border-0 last:pb-0"
                  >
                    <span className="text-sm text-[#a0aabf]">{k}</span>
                    <span className="font-mono text-sm text-white">{v}</span>
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-5 border-t border-white/5 flex items-center gap-2 text-xs text-[#a0aabf]">
                <ShieldCheck className="w-4 h-4 text-[#22c55e]" />
                <span>Garantía técnica · Reporte certificado</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
